import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/client';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT } from './designTokens';

interface TableNamesViewProps {
  refreshKey: number;
}

interface TableRow {
  id: string;
  fullTableName: string;
  shortName: string;
  tableNumber: number;
  zoneName: string;
}

type SortKey = 'fullTableName' | 'shortName' | 'tableNumber' | 'zoneName';
type SortDir = 'asc' | 'desc';

export function TableNamesView({ refreshKey }: TableNamesViewProps) {
  const [tables, setTables] = useState<TableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>('tableNumber');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient<Array<{ id: string; tableNumber: number; tableName: string; zone: { name: string } }>>('/table-management/tables');
      setTables(data.map((t) => {
        const dashIdx = t.tableName.indexOf('-');
        const shortName = dashIdx >= 0 ? t.tableName.substring(0, dashIdx).trim() : t.tableName;
        return { id: t.id, fullTableName: t.tableName, shortName, tableNumber: t.tableNumber, zoneName: t.zone.name };
      }));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [refreshKey]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = [...tables].sort((a, b) => {
    let cmp = 0;
    if (sortKey === 'tableNumber') cmp = a.tableNumber - b.tableNumber;
    else if (sortKey === 'fullTableName') cmp = a.fullTableName.localeCompare(b.fullTableName);
    else if (sortKey === 'shortName') cmp = a.shortName.localeCompare(b.shortName);
    else cmp = a.zoneName.localeCompare(b.zoneName);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const handlePrint = () => {
    const rows = sorted.map((t) => `<tr><td>${t.tableNumber}</td><td>${t.shortName}</td><td>${t.fullTableName}</td><td>${t.zoneName}</td></tr>`).join('');
    const html = `<!DOCTYPE html><html><head><title>Table Names Directory</title><style>body{font-family:sans-serif;padding:20px;}table{width:100%;border-collapse:collapse;}th{text-align:left;padding:8px 10px;border-bottom:2px solid #333;font-size:13px;}td{padding:6px 10px;border-bottom:1px solid #ddd;font-size:12px;}h1{font-size:18px;margin-bottom:4px;}p{color:#666;font-size:12px;margin-bottom:16px;}</style></head><body><h1>Table Names Directory</h1><p>${tables.length} tables • Sorted by ${sortKey === 'tableNumber' ? 'Table Number' : sortKey === 'shortName' ? 'Table Name' : sortKey === 'fullTableName' ? 'Full Table Name' : 'Zone'} (${sortDir}) • Printed ${new Date().toLocaleDateString()}</p><table><thead><tr><th>Table #</th><th>Table Name</th><th>Full Table Name</th><th>Zone</th></tr></thead><tbody>${rows}</tbody></table></body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300); }
  };

  const handleDownload = () => {
    const header = 'Table Number,Table Name,Full Table Name,Zone\n';
    const rows = sorted.map((t) => `${t.tableNumber},"${t.shortName}","${t.fullTableName}","${t.zoneName}"`).join('\n');
    const csv = header + rows;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `table-names-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: TEXT_MUTED }}>Loading table names...</div>;

  const thStyle = (key: SortKey): React.CSSProperties => ({
    textAlign: 'left',
    padding: '0.6rem 0.75rem',
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e0e0e0',
    fontWeight: 700,
    fontSize: '0.8rem',
    color: sortKey === key ? PRIMARY : '#333',
    cursor: 'pointer',
    userSelect: 'none',
  });

  return (
    <div>
      {/* Actions bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ fontSize: '0.82rem', color: TEXT_MUTED }}>
          {tables.length} tables • Sorted by <strong style={{ color: TEXT_DARK }}>{sortKey === 'tableNumber' ? 'Table #' : sortKey === 'shortName' ? 'Name' : sortKey === 'fullTableName' ? 'Full Name' : 'Zone'}</strong> ({sortDir})
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={handlePrint} style={{ padding: '6px 14px', fontSize: '0.8rem', fontWeight: 600, backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }} aria-label="Print table names">
            🖨️ Print
          </button>
          <button onClick={handleDownload} style={{ padding: '6px 14px', fontSize: '0.8rem', fontWeight: 600, backgroundColor: '#1565c0', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }} aria-label="Download CSV">
            📥 Download CSV
          </button>
        </div>
      </div>

      {/* Sortable table */}
      <div style={{ overflowX: 'auto', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }} aria-label="Table names directory">
          <thead>
            <tr>
              <th style={thStyle('tableNumber')} onClick={() => handleSort('tableNumber')}>
                Table # {sortKey === 'tableNumber' && (sortDir === 'asc' ? '▲' : '▼')}
              </th>
              <th style={thStyle('shortName')} onClick={() => handleSort('shortName')}>
                Table Name {sortKey === 'shortName' && (sortDir === 'asc' ? '▲' : '▼')}
              </th>
              <th style={thStyle('fullTableName')} onClick={() => handleSort('fullTableName')}>
                Full Table Name {sortKey === 'fullTableName' && (sortDir === 'asc' ? '▲' : '▼')}
              </th>
              <th style={thStyle('zoneName')} onClick={() => handleSort('zoneName')}>
                Zone {sortKey === 'zoneName' && (sortDir === 'asc' ? '▲' : '▼')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((t) => (
              <tr key={t.id} style={{ borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: TEXT_DARK }}>{t.tableNumber}</td>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: TEXT_DARK }}>{t.shortName}</td>
                <td style={{ padding: '0.5rem 0.75rem', color: TEXT_MUTED, fontSize: '0.82rem' }}>{t.fullTableName}</td>
                <td style={{ padding: '0.5rem 0.75rem', color: TEXT_MUTED }}>{t.zoneName}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
